import axios, { AxiosInstance, AxiosPromise } from 'axios';
import {
  IServerUser,
  ITwoFactorAuthenticationParams,
  IUser,
} from 'store/users/usersType';
import { IThisUserServer } from 'store/thisUser/thisUserSlice';
import {
  ICompany,
  ICompanyMember,
  ICompanyPermissionScheme,
  ICompanyRoleServer,
  ICompanyTemplate,
  IInvitedMember,
  IProject,
  IProjectMemberView,
  IProjectTemplate,
  IUserRoleView,
} from 'store/companies/companiesTypes';
import { ISearchParams } from 'store/entity/types';
import { ITask } from 'store/tasks/tasksType';
import { IProvider, IProviderItemView } from 'store/providers/providerTypes';
import {
  IMultipleAnswerQuestionView,
  IQuestionsView,
  ISurveyViewServer,
} from 'store/survey/surveyTypes';
import { ILimitation } from 'store/limitations/limitationsSlice';

let MakhsanApi: AxiosInstance;
let ManApi: AxiosInstance;
let surveyApi: AxiosInstance;

export const setBaseUrl = (url: string) => {
  MakhsanApi = axios.create({
    baseURL: url + '/makh/rest',
    //  timeout: 125000,
  });
  ManApi = axios.create({
    baseURL: url + '/man/rest',
    //  timeout: 125000,
  });
  surveyApi = axios.create({
    baseURL: url + '/man/rest',
    //  timeout: 125000,
  });
};

export const apis = {
  authenticate2FA: (
    phone: string | null,
    email: string | null,
    password: string | null,
    staySignedIn: boolean = false,
    dryRun: boolean = false
  ): AxiosPromise<ITwoFactorAuthenticationParams> => {
    return MakhsanApi.post(
      '/user/2FAEntrance',
      {},
      {
        params: {
          phone,
          password,
          email,
          dryRun,
          staySignedIn,
        },
      }
    );
  },
  enterWithSingleAccessCode: (
    accessCode: string,
    staySignedIn: boolean
  ): AxiosPromise<IThisUserServer> => {
    return MakhsanApi.get('/user/enterWithSingleAccessCode', {
      params: {
        access_code: accessCode,
        staySignedIn,
      },
    });
  },

  getUserStatus: (access_token: string): AxiosPromise<IServerUser> => {
    return ManApi.get('/user/status', {
      params: {
        access_token,
      },
    });
  },
  userUpdate: (access_token: string, user: IUser) => {
    return ManApi.post('/user/update', user, {
      params: {
        access_token,
      },
    });
  },

  secureRegister: (email: string, password: string, captcha: string) => {
    return MakhsanApi.get('/user/secureRegister', {
      params: {
        email,
        'g-recaptcha-response': captcha,
        password,
      },
    });
  },

  registerUser: (
    access_token: string,
    user: IUser
  ): AxiosPromise<IServerUser> => {
    return ManApi.post('/user/register', user, {
      params: { access_token },
    });
  },

  getAllCompany: (
    access_token: string,
    companyStub: boolean = true
  ): AxiosPromise<ICompany[]> => {
    return ManApi.get('company/getMyCompanies', {
      params: {
        access_token,
        companyStub,
      },
    });
  },
  createCompany: (
    company: ICompany,
    access_token: string
  ): AxiosPromise<ICompany> => {
    return ManApi.post(
      'company/create',
      {
        ...company,
      },
      {
        params: {
          access_token,
        },
      }
    );
  },
  companyAddMembers: (
    invitedMembers: IInvitedMember[],
    forceRename: boolean,
    companyId: number,
    access_token: string
  ): AxiosPromise<IUser[]> => {
    return ManApi.post('company/addMembers', invitedMembers, {
      params: {
        companyId,
        forceRename,
        access_token,
      },
    });
  },
  user_getAll: (
    userIds: number[],
    access_token: string
  ): AxiosPromise<IUser[]> => {
    return ManApi.post('/user/getAll', userIds, {
      params: {
        access_token,
      },
    });
  },
  addTemplateToProject: (
    projectTemplate: IProjectTemplate,
    access_token: string
  ): AxiosPromise<IProjectTemplate> => {
    delete (projectTemplate as any).task;
    return ManApi.post(
      'project/addTemplate',
      {
        ...projectTemplate,
      },
      {
        params: {
          access_token,
        },
      }
    );
  },
  addProjectMember: (
    projectMember: IProjectMemberView,
    access_token: string
  ): AxiosPromise<IProjectMemberView> => {
    return ManApi.post(
      'project/addMember',
      {
        ...projectMember,
      },
      {
        params: {
          access_token,
        },
      }
    );
  },
  createProject: (
    project: IProject,
    access_token: string
  ): AxiosPromise<IProject> => {
    return ManApi.post(
      'project/create',
      {
        ...project,
      },
      {
        params: {
          access_token,
        },
      }
    );
  },
  searchTasks: (
    searchParams: ISearchParams,
    access_token: string
  ): AxiosPromise<ITask[]> => {
    const { fieldValues, ...querySearchParams } = searchParams;
    const data = new FormData();
    data.append(
      'fieldMap',
      new Blob([JSON.stringify(fieldValues || {})], {
        type: 'application/json',
      })
    );
    return ManApi.post('task/search', data, {
      params: {
        ...querySearchParams,
        access_token,
        includeAssignmentHistory: false,
        includeProgressHistory: false,
      },
    });
  },
  updateTask: (task: ITask, access_token: string): AxiosPromise<ITask> => {
    const config = {
      headers: {
        Accept: 'application-v2/json',
      },
      params: {
        access_token,
      },
    };
    return ManApi.post('task/update', task, config);
  },
  createTask: (task: ITask, access_token: string): AxiosPromise<ITask> => {
    const config = {
      headers: {
        Accept: 'application-v2/json',
      },
      params: {
        access_token,
      },
    };
    return ManApi.post('task/create', task, config);
  },
  updateCompany: (
    company: ICompany,
    access_token: string
  ): AxiosPromise<ICompany> => {
    return ManApi.post(
      'company/update',
      {
        ...company,
      },
      {
        params: {
          access_token,
        },
      }
    );
  },
  getProviderStatus: (access_token: string): AxiosPromise<IProvider> => {
    return ManApi.get('provider/status', {
      params: {
        access_token,
      },
    });
  },
  provider_register(provider: IProvider, access_token: string) {
    return ManApi.post(
      'provider/register',
      {
        ...provider,
      },
      {
        params: {
          access_token,
        },
      }
    );
  },
  provider_update(provider: IProvider, access_token: string) {
    return ManApi.post(
      'provider/update',
      {
        ...provider,
      },
      {
        params: {
          access_token,
        },
      }
    );
  },
  tag_getOrCreateTags: (
    access_token: string,
    providerId: number,
    tags: string[],
    parentId: number | null
  ) => {
    return ManApi.get('tag/getOrCreateTags', {
      params: {
        parentId,
        tags,
        providerId,
        access_token,
      },
    });
  },

  getCompany: (
    access_token: string,
    companyId: number,
    templateStub: boolean | null = null,
    companyStub: boolean | null = null,
    projectStub: boolean | null = null,
    taskStub: boolean | null = null
  ): AxiosPromise<ICompany> => {
    return ManApi.get('company/get', {
      params: {
        companyId,
        companyStub,
        projectStub,
        taskStub,
        templateStub,
        access_token,
      },
    });
  },
  getSurvey: (
    surveyToken?: string,
    reportToken?: string,
    ownerToken?: string,
    surveyId?: number
  ): AxiosPromise<ISurveyViewServer> => {
    return surveyApi.get('/survey/getSurvey', {
      params: {
        survey_token: surveyToken,
        report_token: reportToken,
        owner_token: ownerToken,
        surveyId,
      },
    });
  },

  surveyUpdateQuestion: (
    question: IQuestionsView | IMultipleAnswerQuestionView,
    surveyToken: string,
    access_token: string
  ) => {
    delete question.indexOrder;
    return surveyApi.post('/survey/updateQuestion', question, {
      params: {
        access_token,
        survey_token: surveyToken,
      },
    });
  },
  getConfig: (
    configName: string,
    access_token: string
  ): AxiosPromise<{ name: string; value: string }> => {
    return surveyApi.get('/config/getConfig', {
      params: {
        configName,
        access_token,
      },
    });
  },
  updateConfig: (
    config: { name: string; value: string },
    access_token: string
  ) => {
    return surveyApi.post('/config/setConfig', config, {
      params: {
        access_token,
      },
    });
  },

  provider_importContentFromRemote: (
    sourceItemId: number,
    targetItemId: number,
    remoteUrl: string,
    readAccessToken: string,
    forceCopy: boolean,
    excludeSrcFolder: boolean,
    access_token: string
  ) => {
    return ManApi.post(
      'provider/importContentFromOtherProvider',
      {
        replaceTags: [],
      },
      {
        params: {
          sourceItemId,
          targetItemId,
          remoteUrl,
          readAccessToken,
          forceCopy,
          excludeSrcFolder,
          access_token,
        },
      }
    );
  },
  provider_getDocumentsInFolder: (
    providerDocumentId: number,
    access_token: string,
    start = 0,
    count = 200
  ): AxiosPromise<IProviderItemView[]> => {
    return ManApi.get('provider/getDocumentsInFolder', {
      params: {
        count,
        providerDocumentId,
        start,
        access_token,
      },
    });
  },
  updateFolder: (item: IProviderItemView, access_token: string) => {
    const config = {
      params: {
        access_token,
      },
    };
    return ManApi.post('provider/updateFolder', item, config);
  },

  createReadLimitation: (
    providerItemId: number,
    access_token: string
  ): AxiosPromise<IProviderItemView> =>
    ManApi.get('provider/createLimitationForProviderItem', {
      params: {
        providerItemId,
        access_token,
      },
    }),

  getLimitation: (
    limitationId: number,
    access_token: string
  ): AxiosPromise<ILimitation> =>
    ManApi.get('permission/getLimitation', {
      params: {
        id: limitationId,
        access_token,
      },
    }),

  permissionAddRole: (
    limitationId: number,
    roleId: number,
    access_token: string
  ) => {
    return ManApi.get('permission/addRole', {
      params: {
        limitationId,
        roleId,
        access_token,
      },
    });
  },

  updatePermissionScheme: (
    companyPermissionScheme: ICompanyPermissionScheme,
    access_token: string
  ): AxiosPromise<ICompanyPermissionScheme> => {
    return ManApi.post(
      'permissionScheme/update',
      {
        ...companyPermissionScheme,
      },
      {
        params: {
          access_token,
        },
      }
    );
  },
  addPermissionScheme: (
    companyPermissionScheme: ICompanyPermissionScheme,
    access_token: string
  ): AxiosPromise<ICompanyPermissionScheme> => {
    return ManApi.post(
      'permissionScheme/add',
      {
        ...companyPermissionScheme,
      },
      {
        params: {
          access_token,
        },
      }
    );
  },
  addRole: (
    companyRole: ICompanyRoleServer,
    access_token: string
  ): AxiosPromise<any> => {
    return ManApi.post(
      'role/add',
      {
        ...companyRole,
      },
      {
        headers: {
          Accept: 'application-v2/json',
        },
        params: {
          access_token,
        },
      }
    );
  },
  addRoles: (
    userRoleViews: IUserRoleView[],
    access_token: string
  ): AxiosPromise<any> => {
    return ManApi.post('role/addUsers', userRoleViews, {
      headers: {
        Accept: 'application/json',
      },
      params: {
        access_token,
      },
    });
  },
  addCompanyMember: (
    companyMember: ICompanyMember,
    access_token: string
  ): AxiosPromise<any> => {
    return ManApi.post('company/addMember', companyMember, {
      headers: {
        Accept: 'application/json',
      },
      params: {
        access_token,
      },
    });
  },
  addCompanyTemplate: (
    companyTask: ICompanyTemplate,
    access_token: string
  ): AxiosPromise<ICompanyTemplate> => {
    return ManApi.post(
      'company/addTemplate',
      {
        ...companyTask,
      },
      {
        params: {
          access_token,
        },
      }
    );
  },
  updateCompanyTemplate: (
    companyTask: ICompanyTemplate,
    access_token: string
  ): AxiosPromise<ICompanyTemplate> => {
    return ManApi.post(
      'company/updateTemplate',
      {
        ...companyTask,
      },
      {
        params: {
          access_token,
        },
      }
    );
  },
  editMakhsanRoleForUser: (
    user_id: number,
    roles: string,
    action: string,
    access_token: string
  ): AxiosPromise<ICompanyPermissionScheme> => {
    return MakhsanApi.get('user/manageRoles', {
      params: {
        access_token,
        user_id,
        roles,
        action,
      },
    });
  },
};
